import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { RegisterObjectType } from '@assetaccountant/api/models/register-object-type';
import { CustomFieldApi } from '@assetaccountant/api/services/custom-field.api';
import { CustomFieldFormArray } from '@assetaccountant/core/models/custom-field-form-model';
import { CustomFieldFormService } from '@assetaccountant/core/services/custom-field-form.service';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

const MAXIMUM_TYPEAHEAD_RESULTS = 10;

@Component({
  selector: 'app-custom-fields-edit',
  templateUrl: './custom-fields-edit.component.html',
  styleUrls: ['./custom-fields-edit.component.scss'],
  standalone: false,
})
export class CustomFieldsEditComponent implements OnInit {
  @Input() objectType: RegisterObjectType;
  @Input() form: CustomFieldFormArray;

  customFieldKeys: string[] = [];

  @ViewChildren('typeAheadInput', { read: NgbTypeahead })
  typeAheadInputRef: QueryList<NgbTypeahead>;

  constructor(
    private customFieldApi: CustomFieldApi,
    private customFieldFormService: CustomFieldFormService,
  ) {}

  ngOnInit(): void {
    this.form = this.form ?? this.customFieldFormService.createFormArray();
    this.loadCustomFieldKeys();
  }

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        this.customFieldKeys
          .filter(key => {
            const isKeyUnused = !this.form.value.some(t => t.label === key);
            const isSearchTermMatch = key.toLowerCase().includes(term.toLowerCase());
            return isKeyUnused && isSearchTermMatch;
          })
          .slice(0, MAXIMUM_TYPEAHEAD_RESULTS),
      ),
    );

  onBlur(index: number) {
    const customFieldControl = this.form.controls[index];
    const labelControl = customFieldControl.controls.label;

    // Auto trim label
    if (labelControl.invalid) {
      labelControl.setValue(labelControl.value.trim());
    }

    // Add a new row if user clicks on the last non-empty row
    if (index === this.form.length - 1) {
      if (labelControl.value) {
        this.customFieldFormService.addFormCustomField(this.form);
      }
    }
  }

  // Needed for typeahead to display correctly
  onLabelFocus($event: Event) {
    $event.stopPropagation();
    $event.target?.dispatchEvent(new Event('input'));
    setTimeout(() => {}, 0);
  }

  delete(index: number) {
    this.customFieldFormService.removeFormCustomField(this.form, index);
  }

  deleteKey(key: string, input: HTMLInputElement) {
    this.closeTypeahead();

    this.customFieldApi
      .deleteKey({ objectType: this.objectType, customFieldKey: key })
      .subscribe(() => {
        // Remove key
        this.customFieldKeys.splice(
          this.customFieldKeys.findIndex(k => k === key),
          1,
        );
        //this.toastr.success(`The label '${key}' successfully removed`);
      });

    // Reopen typeahead
    input.dispatchEvent(new Event('input'));
  }

  private closeTypeahead() {
    this.typeAheadInputRef.forEach(x => {
      if (x.isPopupOpen()) {
        x.dismissPopup();
      }
    });
  }

  private loadCustomFieldKeys() {
    this.customFieldApi.getKeys({ objectType: this.objectType }).subscribe(response => {
      this.customFieldKeys = response.result?.map(key => key.trim()) ?? [];
    });
  }
}
